import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageSeo from "../../components/common/PageSeo";
import api from "../../utils/api/axiosInstance";
import { useUserAuth } from "../../contexts/UserAuthContext";
import Logo from "../../components/common/Logo";
import IdentityOnboardingChat from "../../components/onboarding/IdentityOnboardingChat";
import {
  SHOPPING_FOR_OPTIONS,
  LIFE_ROLE_OPTIONS,
  TECH_RELATIONSHIP_OPTIONS,
  ECOSYSTEM_OPTIONS,
  DEAL_BREAKER_SUGGESTIONS,
} from "../../constants/shoppingIdentity";
import styles from "../../styles/pages/onboarding/ShoppingProfileOnboardingPage.module.css";

const STEPS = [
  {
    id: "shoppingFor",
    title: "Who do you usually shop for?",
    hint: "We use this to tune match scores and gift vs personal picks.",
  },
  {
    id: "budget",
    title: "What's your real budget ceiling?",
    hint: "Your honest max — not the wishlist price. We'll respect it in search.",
  },
  {
    id: "aboutYou",
    title: "Tell us a bit about you",
    hint: "Pick at least one — life context or how you use tech.",
  },
  {
    id: "preferences",
    title: "Almost done",
    hint: "Optional — helps us avoid bad fits.",
  },
];

const BUDGET_PRESETS = [300, 500, 750, 1000, 1500, 2500];

const parseStepFromSearch = (search) => {
  const raw = new URLSearchParams(search).get("step");
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 1 && n <= STEPS.length) return n - 1;
  return 0;
};

const parseModeFromSearch = (search) => {
  const params = new URLSearchParams(search);
  return params.get("mode") === "chat" ? "chat" : "form";
};

const buildSearchFromState = (mode, stepIndex) => {
  const params = new URLSearchParams();
  if (mode === "chat") {
    params.set("mode", "chat");
  } else if (stepIndex > 0) {
    params.set("step", String(stepIndex + 1));
  }
  const search = params.toString();
  return search ? `?${search}` : "";
};

const ShoppingProfileOnboardingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useUserAuth();
  const [stepIndex, setStepIndex] = useState(() =>
    parseStepFromSearch(location.search),
  );
  const [viewMode, setViewMode] = useState(() =>
    parseModeFromSearch(location.search),
  );
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [completeness, setCompleteness] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    shoppingFor: "",
    budgetMax: "",
    lifeRole: "",
    techRelationship: "",
    ecosystem: "agnostic",
    dealBreakers: [],
  });

  const step = STEPS[stepIndex];
  const progressPct = Math.round(((stepIndex + 1) / STEPS.length) * 100);

  const rawName =
    user?.firstName || user?.displayName || user?.username || "";
  const displayName = rawName.trim() || "there";

  const syncUrlParams = useCallback(
    (mode, nextStepIndex) => {
      const nextSearch = buildSearchFromState(mode, nextStepIndex);
      if (nextSearch !== location.search) {
        navigate(
          { pathname: location.pathname, search: nextSearch },
          { replace: true, state: location.state },
        );
      }
    },
    [navigate, location.pathname, location.search, location.state],
  );

  const changeViewMode = useCallback(
    (mode) => {
      setViewMode(mode);
      syncUrlParams(mode, stepIndex);
    },
    [stepIndex, syncUrlParams],
  );

  const changeStepIndex = useCallback(
    (nextIndex) => {
      setStepIndex(nextIndex);
      syncUrlParams(viewMode, nextIndex);
    },
    [viewMode, syncUrlParams],
  );

  useEffect(() => {
    if (user && !user.needsShoppingProfileOnboarding) {
      navigate("/community", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const mode = parseModeFromSearch(location.search);
    setViewMode(mode);
    if (mode === "form") {
      setStepIndex(parseStepFromSearch(location.search));
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/users/me/onboarding/shopping-profile");
        if (cancelled) return;
        const id = res.data?.identity || {};
        setForm({
          shoppingFor: id.shoppingFor || "",
          budgetMax: id.budgetMax ?? "",
          lifeRole: id.lifeRole || "",
          techRelationship: id.techRelationship || "",
          ecosystem: id.ecosystem || "agnostic",
          dealBreakers: id.dealBreakers || [],
        });
        setCompleteness(res.data?.completeness || null);
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) {
          setLoading(false);
          setProfileLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const canContinue = useMemo(() => {
    if (step.id === "shoppingFor") return Boolean(form.shoppingFor);
    if (step.id === "budget") {
      const n = parseInt(form.budgetMax, 10);
      return Number.isFinite(n) && n > 0;
    }
    if (step.id === "aboutYou") {
      return Boolean(form.lifeRole || form.techRelationship);
    }
    return true;
  }, [step.id, form]);

  const buildPayload = () => ({
    shoppingFor: form.shoppingFor,
    budgetMax:
      form.budgetMax === "" ? null : parseInt(form.budgetMax, 10) || null,
    lifeRole: form.lifeRole || null,
    techRelationship: form.techRelationship || null,
    ecosystem: form.ecosystem || "agnostic",
    dealBreakers: form.dealBreakers,
  });

  const patchIdentity = async () => {
    const res = await api.patch("/users/me/shopping-identity", buildPayload());
    return res.data;
  };

  const finishAndGo = (data) => {
    if (user) {
      setUser({
        ...user,
        needsShoppingProfileOnboarding: Boolean(data?.needsShoppingProfileOnboarding),
        shoppingIdentity: data?.identity || data?.shoppingIdentity,
      });
    }
    const from = location.state?.from;
    navigate(typeof from === "string" && from !== "/onboarding/shopping-profile"
      ? from
      : "/community", { replace: true });
  };

  const handleChatComplete = (data) => {
    finishAndGo({
      identity: data?.identity,
      needsShoppingProfileOnboarding: data?.needsShoppingProfileOnboarding ?? false,
    });
  };

  const handleContinue = async () => {
    if (!canContinue) return;
    setError("");
    try {
      setSaving(true);
      const data = await patchIdentity();
      if (stepIndex < STEPS.length - 1) {
        changeStepIndex(stepIndex + 1);
        return;
      }
      finishAndGo(data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Could not save your answers. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    setError("");
    if (stepIndex > 0) changeStepIndex(stepIndex - 1);
  };

  const handleSkip = async () => {
    setError("");
    try {
      setSaving(true);
      await api.post("/users/me/onboarding/shopping-profile/skip");
      if (user) {
        setUser({ ...user, needsShoppingProfileOnboarding: false });
      }
      const from = location.state?.from;
      navigate(
        typeof from === "string" && from !== "/onboarding/shopping-profile"
          ? from
          : "/community",
        { replace: true },
      );
    } catch (err) {
      setError(err.response?.data?.message || "Could not skip right now.");
    } finally {
      setSaving(false);
    }
  };

  const selectPill = (field, value, { allowDeselect = true } = {}) => {
    setForm((p) => ({
      ...p,
      [field]: allowDeselect && p[field] === value ? "" : value,
    }));
  };

  const toggleDealBreaker = (label) => {
    setForm((p) => {
      const has = p.dealBreakers.includes(label);
      return {
        ...p,
        dealBreakers: has
          ? p.dealBreakers.filter((d) => d !== label)
          : [...p.dealBreakers, label].slice(0, 8),
      };
    });
  };

  return (
    <div className={styles.page}>
      <PageSeo
        title="Set up your shopping profile"
        description="Personalize Simpary recommendations with a short shopping profile."
        pathname="/onboarding/shopping-profile"
        noIndex
      />
      <div className={styles.shell}>
        <div className={styles.brand}>
          <Logo />
          <h1>Hi {displayName}, let&apos;s personalize Simpary</h1>
          <p>
            A quick shopping profile — like Spotify&apos;s taste quiz — so your
            matches reflect who you are, not just what you clicked.
          </p>
        </div>

        <div className={styles.modeToggle} role="tablist" aria-label="Onboarding method">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "form"}
            className={`${styles.modeBtn} ${viewMode === "form" ? styles.modeBtnOn : ""}`}
            onClick={() => changeViewMode("form")}
          >
            Quick form
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "chat"}
            className={`${styles.modeBtn} ${viewMode === "chat" ? styles.modeBtnOn : ""}`}
            onClick={() => changeViewMode("chat")}
          >
            Chat with Simpary
          </button>
        </div>

        {loading ? (
          <div
            className={`${styles.card} ${styles.loadingCard}`}
            aria-busy="true"
            aria-label="Loading your profile"
          >
            <div className={styles.skeletonLineWide} />
            <div className={styles.skeletonLineFull} />
            <div className={styles.skeletonPills}>
              <div className={styles.skeletonPill} />
              <div className={styles.skeletonPill} />
              <div className={styles.skeletonPill} />
            </div>
          </div>
        ) : viewMode === "chat" ? (
          <div className={styles.card}>
            <h2 className={styles.stepTitle}>Tell Simpary how you shop</h2>
            <p className={styles.stepHint}>
              Answer a few questions in your own words — we&apos;ll save your profile as you go.
            </p>
            <IdentityOnboardingChat
              displayName={displayName}
              prefillPrompt={location.state?.prefillQuestion || null}
              skipProfileFetch={profileLoaded}
              initialCompleteness={completeness}
              onComplete={handleChatComplete}
            />
            <div className={styles.secondaryRow}>
              <button
                type="button"
                className={`${styles.textBtn} ${styles.textBtnAccent}`}
                onClick={handleSkip}
                disabled={saving}
              >
                Skip for now
              </button>
            </div>
          </div>
        ) : (
        <>
        <div className={styles.progressWrap}>
          <div className={styles.progressMeta}>
            <span>
              Step {stepIndex + 1} of {STEPS.length}
            </span>
            <span>{progressPct}%</span>
          </div>
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Step ${stepIndex + 1} of ${STEPS.length}`}
          >
            <div
              className={styles.progressFill}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className={styles.card}>
          <h2 className={styles.stepTitle}>{step.title}</h2>
          <p className={styles.stepHint}>{step.hint}</p>

          {step.id === "shoppingFor" && (
            <div className={styles.pillGrid} role="group" aria-label={step.title}>
              {SHOPPING_FOR_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`${styles.pill} ${
                    form.shoppingFor === o.value ? styles.pillOn : ""
                  }`}
                  aria-pressed={form.shoppingFor === o.value}
                  onClick={() =>
                    selectPill("shoppingFor", o.value, { allowDeselect: false })
                  }
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {step.id === "budget" && (
            <div className={styles.fieldBlock}>
              <label className={styles.fieldLabel} htmlFor="budgetMax">
                Max budget (USD)
              </label>
              <input
                id="budgetMax"
                className={styles.budgetInput}
                type="number"
                min="1"
                inputMode="numeric"
                value={form.budgetMax}
                onChange={(e) =>
                  setForm((p) => ({ ...p, budgetMax: e.target.value }))
                }
                placeholder="e.g. 700"
              />
              <div className={styles.budgetPresets}>
                {BUDGET_PRESETS.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    className={`${styles.pill} ${
                      String(form.budgetMax) === String(amount)
                        ? styles.pillOn
                        : ""
                    }`}
                    aria-pressed={String(form.budgetMax) === String(amount)}
                    onClick={() =>
                      setForm((p) => ({ ...p, budgetMax: String(amount) }))
                    }
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step.id === "aboutYou" && (
            <>
              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>Life context</span>
                <div className={styles.pillGrid} role="group" aria-label="Life context">
                  {LIFE_ROLE_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={`${styles.pill} ${
                        form.lifeRole === o.value ? styles.pillOn : ""
                      }`}
                      aria-pressed={form.lifeRole === o.value}
                      onClick={() => selectPill("lifeRole", o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>How you use tech</span>
                <div className={styles.pillGrid} role="group" aria-label="How you use tech">
                  {TECH_RELATIONSHIP_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={`${styles.pill} ${
                        form.techRelationship === o.value ? styles.pillOn : ""
                      }`}
                      aria-pressed={form.techRelationship === o.value}
                      onClick={() => selectPill("techRelationship", o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step.id === "preferences" && (
            <>
              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>Ecosystem</span>
                <div className={styles.pillGrid} role="group" aria-label="Ecosystem">
                  {ECOSYSTEM_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={`${styles.pill} ${
                        form.ecosystem === o.value ? styles.pillOn : ""
                      }`}
                      aria-pressed={form.ecosystem === o.value}
                      onClick={() =>
                        setForm((p) => ({ ...p, ecosystem: o.value }))
                      }
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>Deal-breakers (optional)</span>
                <div className={styles.pillGrid} role="group" aria-label="Deal-breakers">
                  {DEAL_BREAKER_SUGGESTIONS.map((label) => (
                    <button
                      key={label}
                      type="button"
                      className={`${styles.pill} ${
                        form.dealBreakers.includes(label) ? styles.pillOn : ""
                      }`}
                      aria-pressed={form.dealBreakers.includes(label)}
                      onClick={() => toggleDealBreaker(label)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {error ? (
            <p className={styles.validation} role="alert">
              {error}
            </p>
          ) : null}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={!canContinue || saving}
              onClick={handleContinue}
            >
              {saving
                ? "Saving…"
                : stepIndex === STEPS.length - 1
                  ? "Finish & see my feed"
                  : "Continue"}
            </button>
            <div className={styles.secondaryRow}>
              {stepIndex > 0 ? (
                <button
                  type="button"
                  className={styles.textBtn}
                  onClick={handleBack}
                  disabled={saving}
                >
                  Back
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                className={`${styles.textBtn} ${styles.textBtnAccent}`}
                onClick={handleSkip}
                disabled={saving}
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ShoppingProfileOnboardingPage;
